import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Divider,
    Grid,
    Card,
    CardContent,
    Container,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    CircularProgress,
    useTheme,
    Stack,
    InputAdornment,
    useMediaQuery,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    Chip
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    Save as SaveIcon,
    Search as SearchIcon,
    ArrowUpward,
    ArrowDownward,
    UnfoldMore,
    SwapCalls,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../../services/api';
import Paginate from '../../../components/Paginate';
import { jwtDecode } from 'jwt-decode';

const itemsPerPage = 6;

const FormsTemplatePage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const location = useLocation();
    const [typeUser, setTypeUser] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [openDialogAssociateForm, setOpenDialogAssociateForm] = useState(false);
    const [typeBusinessOptions, setTypeBusinessOptions] = useState([]);
    const [formDataAssociate, setFormDataAssociate] = useState({
        formTemplateId: null,
        typeBusinessIds: []
    });
    const [currentTemplate, setCurrentTemplate] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        createdBy: '',
        enable: 1,
    });
    const [initialFormData, setInitialFormData] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [page, setPage] = useState(1);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [templateToDeleteId, setTemplateToDeleteId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'ascending',
    });

    useEffect(() => {
        fetchAllTemplates();
    }, []);

    useEffect(() => {
        if (openDialogAssociateForm) {
            fetchTypeBusinessOptions();
        }
    }, [openDialogAssociateForm]);

    useEffect(() => {
        fetchAllTemplates();
        setTypeUser(location.pathname.split('/')[1]);
    }, [location.pathname]);

    const fetchTypeBusinessOptions = async () => {
        try {
            const response = await api.get('/type-business');
            const data = await response.data;
            setTypeBusinessOptions(data);
        } catch (error) {
            showSnackbar('Erro ao carregar tipos de negócio', 'error');
            console.error('Erro ao carregar tipos de negócio:', error);
        }
    };

    const fetchAllTemplates = async () => {
        try {
            setLoading(true);
            const response = await api.get('/form-templates');
            setTemplates(response.data);
        } catch (error) {
            showSnackbar('Erro ao carregar formulários', 'error');
            console.error('Error fetching templates:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };
    const normalizeString = (str) => {
        if (typeof str !== 'string') {
            return '';
        }
        return str
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/\s/g, "")
            .toLowerCase();
    };

    const visibleTemplates = useMemo(() => {
        const normalizedSearchTerm = normalizeString(searchTerm);

        let filteredTemplates = templates.filter(template =>
            normalizeString(template.name).includes(normalizedSearchTerm)
        );

        if (sortConfig.key) {
            filteredTemplates.sort((a, b) => {
                const valA = (sortConfig.key === 'name') ? normalizeString(a[sortConfig.key]) : a[sortConfig.key];
                const valB = (sortConfig.key === 'name') ? normalizeString(b[sortConfig.key]) : b[sortConfig.key];

                if (valA < valB) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (valA > valB) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }

        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredTemplates.slice(startIndex, endIndex);
    }, [templates, page, searchTerm, sortConfig]);

    const handleOpenDialog = (template = null) => {
        setCurrentTemplate(template);
        const token = localStorage.getItem('token');
        const decode = jwtDecode(token);
        const newFormData = {
            name: template ? template.name : '',
            description: template ? template.description : '',
            createdBy: template ? template.createdBy : decode.id,
            enable: template ? (template.enable ? 1 : 0) : 1,
        };
        setFormData(newFormData);
        setInitialFormData(newFormData);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCurrentTemplate(null);
        setFormData({ name: '', description: '', createdBy: '', enable: 1 });
        setInitialFormData(null);
    };

    const handleOpenDialogAssociateForm = (template) => {
        let ids = template?.typeBusiness?.map(type => type.id) || [];
        setFormDataAssociate({
            formTemplateId: template?.id || null,
            typeBusinessIds: ids
        });
        setOpenDialogAssociateForm(true);
    };

    const handleSubmitAssociation = async () => {
        try {
            await api.post('/form-business', formDataAssociate);
            showSnackbar("Formulário associado com sucesso.", 'success');
            handleClodeDialogAssociateForm();
        } catch (error) {
            const message = error.response?.data?.error || 'Erro ao processar a solicitação';
            showSnackbar(message, 'error');
            console.error('Error submitting template:', error);
        }
    };

    const handleClodeDialogAssociateForm = () => {
        setOpenDialogAssociateForm(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            if (currentTemplate) {
                await api.put(`/form-templates/${currentTemplate.id}`, formData);
                showSnackbar('Formulário atualizado com sucesso.', 'success');
            } else {
                const creationDate = new Date().toLocaleDateString('pt-BR');
                await api.post('/form-templates', { ...formData, creationDate });
                showSnackbar('Formulário cadastrado com sucesso.', 'success');
            }
            fetchAllTemplates();
            handleCloseDialog();
        } catch (error) {
            const message = error.response?.data?.error || 'Erro ao processar a solicitação';
            showSnackbar(message, 'error');
            console.error('Error submitting template:', error);
        }
    };

    const handleDelete = (id) => {
        setTemplateToDeleteId(id);
        setOpenConfirmDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await api.delete(`/form-templates/${templateToDeleteId}`);
            showSnackbar('Formulário excluído com sucesso.', 'success');

            const response = await api.get('/form-templates');
            setTemplates(response.data);

            const totalPages = Math.ceil(response.data.length / itemsPerPage);

            if (page > totalPages && page > 1) {
                setPage(page - 1);
            }
        } catch (error) {
            showSnackbar('Erro ao excluir formulário.', 'error');
            console.error('Error deleting template:', error);
        } finally {
            setOpenConfirmDialog(false);
            setTemplateToDeleteId(null);
        }
    };

    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
        setTemplateToDeleteId(null);
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setPage(1);
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const handleManageItems = (template) => {
        navigate(`/${typeUser}/form-builder/${template.id}`);
    };

    const handleViewResponses = (template) => {
        navigate(`/${typeUser}/forms-view-responses/${template.id}`);
    };

    const hasFormChanged = useMemo(() => {
        if (!initialFormData) return false;
        return (
            formData.name !== initialFormData.name ||
            formData.description !== initialFormData.description ||
            formData.enable !== initialFormData.enable
        );
    }, [formData, initialFormData]);

    const isSubmitDisabled = !formData.name || !formData.description || (currentTemplate && !hasFormChanged);

    return (
        <ThemeProvider theme={theme}>
            <Container maxWidth={isMobile ? 'xs' : 'xl'} sx={{ mt: isMobile ? 1 : 0, mb: 4, px: isMobile ? 1 : 2 }}>
                <Grid container spacing={isMobile ? 1 : 3} sx={{ justifyContent: 'center', mt: 0 }}>
                    <Grid item xs={12} sm={10} md={8}>
                        <Card sx={{
                            borderRadius: isMobile ? '8px' : '12px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                            p: isMobile ? 1 : 2,
                            mt: 0
                        }}>
                            <CardContent sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                pt: isMobile ? 0 : 0
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: isMobile ? 'column' : 'row',
                                    justifyContent: 'space-between',
                                    alignItems: isMobile ? 'center' : 'center',
                                    mb: isMobile ? 2 : 3,
                                    width: '100%',
                                    gap: isMobile ? 2 : 0
                                }}>
                                    <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ color: '#10641c', fontWeight: '600' }}>
                                        Formulários
                                    </Typography>
                                    {localStorage.getItem('role') === 'city_hall' ? null : (
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={() => handleOpenDialog()}
                                            sx={{
                                                bgcolor: '#10641c',
                                                '&:hover': { bgcolor: '#0a3d0f' },
                                                fontSize: isMobile ? '0.8rem' : '0.875rem',
                                                px: isMobile ? 2 : 3
                                            }}
                                        >
                                            Novo Formulário
                                        </Button>
                                    )}

                                </Box>

                                <Divider sx={{ mb: isMobile ? 2 : 2, width: '100%' }} />
                                <Box sx={{ width: '100%', mb: isMobile ? 2 : 2 }}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Buscar por formulário..."
                                        value={searchTerm}
                                        onChange={handleSearch}
                                        sx={{
                                            height: '45px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: isMobile ? '6px' : '8px',
                                                fontSize: isMobile ? '0.9rem' : '1rem',
                                                height: 'inherit',
                                                padding: '0 14px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#ced4da',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#10641c',
                                                },
                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: '#10641c',
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: isMobile ? '0.9rem' : '1rem'
                                                }
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize={isMobile ? 'small' : 'medium'} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>

                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: isMobile ? 2 : 4 }}>
                                        <CircularProgress size={isMobile ? 30 : 40} />
                                    </Box>
                                ) : (
                                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                        <TableContainer component={Paper} sx={{ maxWidth: '100%' }}>
                                            <Table sx={{
                                                minWidth: isMobile ? 'auto' : 900,
                                                '& .MuiTableCell-head': { fontWeight: 'bold' },
                                                '& .MuiTableCell-root': {
                                                    fontSize: isMobile ? '0.8rem' : '0.875rem',
                                                    p: isMobile ? 1 : 1
                                                }
                                            }} aria-label="templates table">
                                                <TableHead sx={{ bgcolor: '#f8f8f8' }}>
                                                    <TableRow>
                                                        <TableCell
                                                            align="center"
                                                            sx={{ width: isMobile ? '40%' : '30%', cursor: 'pointer' }}
                                                            onClick={() => requestSort('name')}
                                                        >
                                                            <Box display="flex" alignItems="center" justifyContent="center">
                                                                Nome
                                                                {sortConfig.key === 'name' ? (
                                                                    sortConfig.direction === 'ascending' ?
                                                                        <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> :
                                                                        <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                                                                ) : (
                                                                    <UnfoldMore fontSize="small" color="action" sx={{ ml: 0.5 }} />
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell
                                                            align="center"
                                                            sx={{ width: isMobile ? '40%' : '50%', cursor: 'pointer' }}
                                                            onClick={() => requestSort('description')}
                                                        >
                                                            <Box display="flex" alignItems="center" justifyContent="center">
                                                                Descrição
                                                                {sortConfig.key === 'description' ? (
                                                                    sortConfig.direction === 'ascending' ?
                                                                        <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> :
                                                                        <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                                                                ) : (
                                                                    <UnfoldMore fontSize="small" color="action" sx={{ ml: 0.5 }} />
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell
                                                            align="center"
                                                            sx={{ width: isMobile ? '20%' : '20%' }}
                                                        >
                                                            Ações
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>

                                                <TableBody>
                                                    {visibleTemplates.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={3} align="center">
                                                                <Typography
                                                                    variant={isMobile ? "body2" : "body1"}
                                                                    sx={{ color: "#666", py: 2 }}
                                                                >
                                                                    Nenhum formulário encontrado.
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        visibleTemplates.map((template) => (
                                                            <TableRow
                                                                key={template.id}
                                                                sx={{
                                                                    "&:last-child td, &:last-child th": { border: 0 },
                                                                    "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                                                                }}
                                                            >
                                                                <TableCell align="center">{template.name}</TableCell>
                                                                <TableCell align="center">
                                                                    {template.description || "Sem descrição"}
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Stack
                                                                        direction={isMobile ? "column" : "row"}
                                                                        spacing={isMobile ? 0.5 : 1}
                                                                        justifyContent="center"
                                                                    >
                                                                        {localStorage.getItem('role') === 'city_hall' ? (
                                                                            <Tooltip title="Visualizar Respostas">
                                                                                <IconButton
                                                                                    onClick={() => handleViewResponses(template)}
                                                                                    sx={{ color: "#1A7F5C", p: isMobile ? 0.5 : 1 }}
                                                                                >
                                                                                    <VisibilityIcon fontSize={isMobile ? "small" : "medium"} />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <>
                                                                                <Tooltip title="Gerenciar Perguntas">
                                                                                    <IconButton
                                                                                        onClick={() => handleManageItems(template)}
                                                                                        sx={{ color: "#4caf50", p: isMobile ? 0.5 : 1 }}
                                                                                    >
                                                                                        <AddIcon fontSize={isMobile ? "small" : "medium"} />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                                <Tooltip title="Editar Formulário">
                                                                                    <IconButton
                                                                                        onClick={() => handleOpenDialog(template)}
                                                                                        sx={{ color: "#1976d2", p: isMobile ? 0.5 : 1 }}
                                                                                    >
                                                                                        <EditIcon fontSize={isMobile ? "small" : "medium"} />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                                <Tooltip title="Associar a Tipo de Empreendimento">
                                                                                    <IconButton
                                                                                        onClick={() => handleOpenDialogAssociateForm(template)}
                                                                                        sx={{ color: "#10641c", p: isMobile ? 0.5 : 1 }}
                                                                                    >
                                                                                        <SwapCalls fontSize={isMobile ? "small" : "medium"} />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                                <Tooltip title="Visualizar Respostas">
                                                                                    <IconButton
                                                                                        onClick={() => handleViewResponses(template)}
                                                                                        sx={{ color: "#1A7F5C", p: isMobile ? 0.5 : 1 }}
                                                                                    >
                                                                                        <VisibilityIcon fontSize={isMobile ? "small" : "medium"} />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                                <Tooltip title="Excluir Formulário">
                                                                                    <IconButton
                                                                                        onClick={() => handleDelete(template.id)}
                                                                                        sx={{ color: "#d32f2f", p: isMobile ? 0.5 : 1 }}
                                                                                    >
                                                                                        <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            </>
                                                                        )}
                                                                    </Stack>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {templates.length > itemsPerPage && (
                    <Box sx={{ mt: isMobile ? 1 : 2, display: 'flex', justifyContent: 'center' }}>
                        <Paginate
                            count={Math.ceil(templates.length / itemsPerPage)}
                            page={page}
                            onChange={handlePageChange}
                            size={isMobile ? 'small' : 'medium'}
                        />
                    </Box>
                )}

                {/* Dialog para adicionar/editar Template */}
                <Dialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    fullWidth
                    maxWidth={isMobile ? 'xs' : 'xs'}
                    fullScreen={isMobile}
                >
                    <DialogTitle
                        sx={{
                            background: 'linear-gradient(45deg, #10641c 30%, #388e3c 90%)',
                            color: 'white',
                            textAlign: 'center'
                        }}
                    >
                        {currentTemplate ? 'Editar Formulário' : 'Cadastrar Formulário'}
                    </DialogTitle>
                    <DialogContent sx={{ mt: isMobile ? 2 : 3, p: isMobile ? 2 : 3, display: 'flex', justifyContent: 'center' }}>
                        <Grid container width={"100%"} spacing={2} direction="column">
                            <Grid item xs={12} sx={{ p: 1 }}>
                                <TextField
                                    fullWidth
                                    label="Nome (Obrigatório)"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#10641c',
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#10641c',
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: isMobile ? '0.9rem' : '1rem'
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sx={{ p: 1 }}>
                                <TextField
                                    fullWidth
                                    label="Descrição (Obrigatório)"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    variant="outlined"
                                    multiline
                                    rows={3}
                                    sx={{
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#10641c',
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#10641c',
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: isMobile ? '0.9rem' : '1rem'
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sx={{ p: 1 }}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel sx={{
                                        '&.Mui-focused': { color: '#10641c' },
                                        fontSize: isMobile ? '0.9rem' : '1rem'
                                    }}>
                                        Situação
                                    </InputLabel>
                                    <Select
                                        value={formData.enable}
                                        onChange={handleChange}
                                        label="Situação"
                                        name="enable"
                                        sx={{
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#10641c',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#10641c',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#10641c',
                                            },
                                            '& .MuiSelect-select': {
                                                fontSize: isMobile ? '0.9rem' : '1rem'
                                            }
                                        }}
                                    >
                                        <MenuItem value={1} sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                                            Ativo
                                        </MenuItem>
                                        <MenuItem value={0} sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                                            Inativo
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: isMobile ? 2 : 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button
                            onClick={handleCloseDialog}
                            sx={{
                                bgcolor: 'red',
                                color: 'white',
                                '&:hover': { bgcolor: '#cc0000' },
                                fontSize: isMobile ? '0.8rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<CloseIcon />}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            disabled={isSubmitDisabled}
                            sx={{
                                bgcolor: '#10641c',
                                color: '#fff',
                                '&:hover': { bgcolor: '#0a3d0f' },
                                '&:disabled': { bgcolor: '#e0e0e0', color: '#9e9e9e' },
                                fontSize: isMobile ? '0.8rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<SaveIcon />}
                        >
                            {currentTemplate ? 'Atualizar' : 'Salvar'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={openDialogAssociateForm}
                    onClose={handleClodeDialogAssociateForm}
                    fullWidth
                    maxWidth={isMobile ? 'xs' : 'xs'}
                    fullScreen={isMobile}
                >
                    <DialogTitle
                        sx={{
                            background: 'linear-gradient(45deg, #10641c 30%, #388e3c 90%)',
                            color: 'white',
                            textAlign: 'center',
                            padding: '16px',
                        }}
                    >
                        Associar Formulário
                    </DialogTitle>
                    <DialogContent sx={{ marginTop: isMobile ? 2 : 3, padding: isMobile ? 2 : 3 }}>
                        <FormControl fullWidth variant="outlined" margin="normal">
                            <InputLabel
                                sx={{
                                    '&.Mui-focused': { color: '#10641c' },
                                    fontSize: isMobile ? '0.875rem' : '1rem',
                                    marginTop: '5px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'visible'
                                }}
                            >
                                Tipos de Empreendimento (Obrigatório)
                            </InputLabel>
                            <Select
                                multiple
                                value={formDataAssociate.typeBusinessIds || []}
                                onChange={(e) => setFormDataAssociate({
                                    ...formDataAssociate,
                                    typeBusinessIds: e.target.value
                                })}
                                label="Tipos de Empreendimento (Obrigatório)"
                                sx={{
                                    borderRadius: '6px',
                                    backgroundColor: '#fff',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#10641c'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#10641c'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#10641c'
                                    },
                                    '& .MuiSelect-select': {
                                        fontSize: isMobile ? '0.875rem' : '1rem'
                                    }
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 220,
                                            width: 220,
                                            maxWidth: '60vw'
                                        }
                                    },
                                    anchorOrigin: {
                                        vertical: 'bottom',
                                        horizontal: 'left'
                                    },
                                    transformOrigin: {
                                        vertical: 'top',
                                        horizontal: 'left'
                                    },
                                    disableScrollLock: true,
                                    style: {
                                        marginTop: isMobile ? '5px' : '0px',
                                        maxHeight: 'calc(100vh - 200px)'
                                    }
                                }}
                                renderValue={(selected) =>
                                    selected.length === 0
                                        ? 'Selecione os tipos de empreendimento'
                                        : typeBusinessOptions
                                            .filter((option) => selected.includes(option.id))
                                            .map((option) => option.name)
                                            .join(', ')
                                }
                            >
                                {typeBusinessOptions
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            <Checkbox
                                                checked={formDataAssociate.typeBusinessIds.includes(option.id)}
                                                sx={{
                                                    color: '#10641c',
                                                    '&.Mui-checked': {
                                                        color: '#10641c'
                                                    }
                                                }}
                                            />
                                            <ListItemText
                                                primary={option.name}
                                                sx={{
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                    maxWidth: '100%'
                                                }}
                                            />
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions sx={{ p: isMobile ? 2 : 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button
                            onClick={handleClodeDialogAssociateForm}
                            sx={{
                                bgcolor: 'red',
                                color: 'white',
                                '&:hover': { bgcolor: '#cc0000' },
                                fontSize: isMobile ? '0.8rem' : '0.875rem',
                                px: isMobile ? 2 : 3,
                            }}
                            startIcon={<CloseIcon />}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSubmitAssociation}
                            variant="contained"
                            disabled={formDataAssociate.typeBusinessIds.length === 0}
                            sx={{
                                bgcolor: '#10641c',
                                color: '#fff',
                                '&:hover': { bgcolor: '#0a3d0f' },
                                fontSize: isMobile ? '0.8rem' : '0.875rem',
                                px: isMobile ? 2 : 3,
                            }}
                            startIcon={<SaveIcon />}
                        >
                            Associar
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Diálogo de confirmação de exclusão */}
                <Dialog
                    open={openConfirmDialog}
                    onClose={handleCloseConfirmDialog}
                    fullWidth
                    maxWidth={isMobile ? 'xs' : 'xs'}
                    fullScreen={isMobile}
                    sx={{ '& .MuiDialog-paper': { width: isMobile ? '100%' : '300px' } }}
                >
                    <DialogTitle sx={{ bgcolor: '#d32f2f', color: 'white', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        Confirmar Exclusão
                    </DialogTitle>
                    <DialogContent sx={{ mt: isMobile ? 1 : 2, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: isMobile ? '1rem' : '1rem' }}>
                            Tem certeza que deseja excluir este formulário? <br /> Esta ação é irreversível.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: isMobile ? 2 : 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button
                            onClick={handleCloseConfirmDialog}
                            sx={{
                                bgcolor: '#10641c',
                                color: 'white',
                                '&:hover': { bgcolor: '#0a3d0f' },
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<CloseIcon />}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleConfirmDelete}
                            color="error"
                            variant="contained"
                            sx={{
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<DeleteIcon />}
                        >
                            Excluir
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar para feedback */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </ThemeProvider>
    );
};

export default FormsTemplatePage;